"use client";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { getSavedConversationId } from "@/lib/chat/client-state";
import { CHAT_CONVERSATION_NOT_OWNED } from "@/lib/auth/constants";

/** A stale local chat must not prevent a separately verified account login. */
export async function fetchAuthWithConversation(
  path:
    | "/api/auth/verify-otp"
    | "/api/auth/phone/login"
    | "/api/auth/email/verify-verification-otp"
    | "/api/auth/prepare-oauth",
  payload: Record<string, unknown>
): Promise<Response> {
  const conversationId = getSavedConversationId();
  const submit = (selectedConversationId?: string) =>
    fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, conversationId: selectedConversationId }),
    });
  const response = await submit(conversationId);
  // This code is emitted only BEFORE authentication. Never retry an auth or
  // post-verification handoff failure without its server-bound continuation.
  if (conversationId && response.status === 403) {
    const data = await response
      .clone()
      .json()
      .catch(() => null);
    if (data?.code === CHAT_CONVERSATION_NOT_OWNED) return submit();
  }
  return response;
}

export async function installAuthSession(
  supabase: SupabaseClient,
  session: Pick<Session, "access_token" | "refresh_token"> | undefined
): Promise<Session> {
  if (!session?.access_token || !session.refresh_token) {
    throw new Error("Die Sitzung konnte nicht geladen werden. Bitte versuchen Sie es erneut.");
  }
  const { data, error } = await supabase.auth.setSession(session);
  if (error) throw new Error(error.message);
  if (!data.session)
    throw new Error("Die Sitzung konnte nicht geladen werden. Bitte versuchen Sie es erneut.");
  return data.session;
}
